import { AnimatePresence, motion } from "motion/react"
import { useCallback, useEffect, useState } from "react"
import { EncryptedText } from "@/components/ui/encrypted-text"
import { neonFillClass, neonGlowClass } from "@/components/ui/neon"
import { SharedButton } from "@/components/ui/shared-button"
import { cn } from "@/lib/utils"

const BG_VIDEO_SRC =
  "https://70fy8xbp6b.ufs.sh/f/BzM3qquY5hLZWzGCJ3Tuw0SRaZ5MmOhKPvlHdeTg63qxUsCD"

const YT_ID = "WKnZx7QnlGU"
const TRAILER_SRC =
  `https://www.youtube-nocookie.com/embed/${YT_ID}` +
  `?autoplay=1&rel=0&modestbranding=1&playsinline=1`

const GRAIN_SVG =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"

export function Hero() {
  const [trailerOpen, setTrailerOpen] = useState(false)
  const closeTrailer = useCallback(() => setTrailerOpen(false), [])

  useEffect(() => {
    if (!trailerOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeTrailer()
    }
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    window.addEventListener("keydown", onKey)
    return () => {
      window.removeEventListener("keydown", onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [trailerOpen, closeTrailer])

  return (
    <section className="relative h-[78dvh] w-full overflow-hidden bg-background text-white sm:h-dvh">
      <video
        src={BG_VIDEO_SRC}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover"
      >
        <track
          default
          kind="captions"
          srcLang="en"
          label="No dialogue"
          src="/captions/empty.vtt"
        />
      </video>

      <div
        className="pointer-events-none absolute inset-0 bg-background/40"
        aria-hidden="true"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 55%, transparent 0%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.95) 100%)",
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-linear-to-b from-black/35 via-transparent to-black/70 sm:from-black/80 sm:to-black/95"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.18] mix-blend-overlay"
        style={{ backgroundImage: GRAIN_SVG, backgroundSize: "240px 240px" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(180deg, rgba(255,255,255,0.5) 0 1px, transparent 1px 3px)",
        }}
      />

      <motion.div
        aria-hidden="true"
        initial={{ height: "50vh" }}
        animate={{ height: 0 }}
        transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
        className="pointer-events-none absolute inset-x-0 top-0 z-30 w-full origin-top bg-background"
      />
      <motion.div
        aria-hidden="true"
        initial={{ height: "50vh" }}
        animate={{ height: 0 }}
        transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
        className="pointer-events-none absolute inset-x-0 bottom-0 z-30 w-full origin-bottom bg-background"
      />

      <div className="absolute inset-x-0 bottom-8 z-40 flex items-end justify-between gap-8 px-6 pb-2 sm:bottom-10 sm:px-10 sm:pb-4">
        <div className="max-w-176">
          <motion.p
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="mb-5 font-mono text-[10px] text-label uppercase tracking-[0.5em]"
          >
            File 01 · Prologue · The End begins
          </motion.p>

          <h1 className="font-medium font-mono text-[clamp(2rem,5.6vw,4.8rem)] text-white uppercase leading-[1.05] tracking-[-0.045em] [word-spacing:-0.2em]">
            <span className="block">
              <EncryptedText
                text="Two return."
                encryptedClassName="text-label"
                revealedClassName="text-white"
                revealDelayMs={70}
                flipDelayMs={45}
                startDelayMs={1500}
              />
            </span>
            <span className="block">
              <EncryptedText
                text="One remains."
                encryptedClassName="text-label"
                revealedClassName="text-white"
                revealDelayMs={70}
                flipDelayMs={45}
                startDelayMs={2500}
              />
            </span>
            <span className={cn("mt-1 block font-bold", neonGlowClass)}>
              <EncryptedText
                text="He awakens."
                encryptedClassName="text-[#3E7BD9]/45"
                revealedClassName={neonFillClass}
                revealDelayMs={90}
                flipDelayMs={45}
                startDelayMs={3600}
              />
            </span>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 4.6 }}
            className="mt-6"
          >
            <SharedButton
              type="button"
              size="lg"
              onClick={() => setTrailerOpen(true)}
              className="inline-flex cursor-pointer items-center gap-2.5 border-2 border-brand bg-transparent text-white shadow-none hover:bg-brand/15"
            >
              <img
                src="/play.svg"
                alt=""
                aria-hidden="true"
                width={14}
                height={14}
                className="size-3.5 brightness-0 invert"
              />
              Watch trailer
            </SharedButton>
          </motion.div>
        </div>
      </div>

      <TrailerLightbox open={trailerOpen} onClose={closeTrailer} />

      <motion.aside
        initial={{ opacity: 0, x: 10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1.2, delay: 1.6 }}
        className="absolute right-6 bottom-10 z-40 hidden text-right font-mono text-[10px] text-label uppercase leading-[1.9] tracking-[0.3em] sm:right-10 sm:bottom-14 md:block"
        aria-label="origin"
      >
        27.9881°N
        <br />
        86.9250°E
        <br />
        <span className="font-devanagari text-white/75 normal-case">
          हिमालय
        </span>{" "}
        · Nepāl
        <br />
        <span className="text-label-dim">last signal</span>
      </motion.aside>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.5, delay: 4.8 }}
        className="absolute inset-x-0 bottom-0 z-40 hidden justify-center pb-5 md:flex"
        aria-hidden="true"
      >
        <div className="relative h-8 w-px bg-linear-to-b from-white/35 via-white/10 to-transparent">
          <motion.span
            className="absolute left-1/2 h-2 w-0.5 -translate-x-1/2 bg-brand shadow-[0_0_8px_rgb(var(--brand-rgb)/0.9)]"
            initial={{ top: "-10%" }}
            animate={{ top: ["-10%", "110%"] }}
            transition={{
              duration: 1.6,
              repeat: Infinity,
              ease: "easeIn",
            }}
          />
        </div>
      </motion.div>
    </section>
  )
}

function TrailerLightbox({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-label="EKLO trailer"
          className="fixed inset-0 z-120 flex items-center justify-center bg-background/90 p-4 backdrop-blur-md sm:p-10"
        >
          <motion.div
            initial={{ scale: 0.97, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.97, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative aspect-video w-full max-w-5xl border border-white/15 bg-black"
          >
            <iframe
              title="EKLO trailer"
              src={TRAILER_SRC}
              className="h-full w-full"
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
            />
            <button
              type="button"
              onClick={onClose}
              aria-label="Close trailer"
              className="absolute -top-2 -right-2 flex size-9 items-center justify-center bg-brand text-lg text-white leading-none transition-colors hover:bg-brand/85 sm:-top-4 sm:-right-4"
            >
              ×
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
